import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { AuthLogoutPage } from '@/features/auth/AuthLogoutPage';
import { AuthErrorPage } from '@/features/auth/AuthErrorPage';
import { ImpersonatePage } from '@/features/auth/ImpersonatePage';
import { ImpersonateStartPage } from '@/features/auth/ImpersonateStartPage';
import { ImpersonateCallbackPage } from '@/features/auth/ImpersonateCallbackPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AppShell } from '@/components/layout/AppShell';
import { ClientsListPage } from '@/features/clients/ClientsListPage';
import { ClientDetailPage } from '@/features/clients/ClientDetailPage';
import { ClientNewPage } from '@/features/clients/ClientNewPage';
import { ClientEditPage } from '@/features/clients/ClientEditPage';
import { SchedulingPage } from '@/features/scheduling/SchedulingPage';
import { SessionPage } from '@/features/sessions/SessionPage';
import { TreatmentPlansPage } from '@/features/treatment-plans/TreatmentPlansPage';
import { ProgramDetailPage } from '@/features/treatment-plans/ProgramDetailPage';
import { PlanBuilderPage } from '@/features/treatment-plans/PlanBuilderPage';
import { ConfigurationPage } from '@/features/configuration/ConfigurationPage';
import { NoteTypeBuilderPage } from '@/features/configuration/NoteTypeBuilderPage';
import { StripeReturnPage } from '@/features/configuration/StripeReturnPage';
import { StripeRefreshPage } from '@/features/configuration/StripeRefreshPage';
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage';
import { BillingLayout } from '@/features/billing/BillingLayout';
import { BillingPage } from '@/features/billing/BillingPage';
import { BillingDetailPage } from '@/features/billing/BillingDetailPage';
import { BillingNewPage } from '@/features/billing/BillingNewPage';
import { BillingSubmissionsPage } from '@/features/billing/BillingSubmissionsPage';
import { BillingSubmissionDetailPage } from '@/features/billing/BillingSubmissionDetailPage';
import { InvoicesTab } from '@/features/billing/InvoicesTab';
import { InvoiceDetailPage } from '@/features/billing/InvoiceDetailPage';
import { ReportingPage } from '@/features/reporting/ReportingPage';
import { BillingArPage } from '@/features/reporting/BillingArPage';
import { SessionActivityPage } from '@/features/reporting/SessionActivityPage';
import { CancellationRollupPage } from '@/features/reporting/CancellationRollupPage';
import { AuthUtilizationPage } from '@/features/reporting/AuthUtilizationPage';
import { StaffPerformancePage } from '@/features/reporting/StaffPerformancePage';
import { PayrollPage } from '@/features/reporting/PayrollPage';
import { AvailabilityOverlapPage } from '@/features/reporting/AvailabilityOverlapPage';
import { SupervisionCompliancePage } from '@/features/reporting/SupervisionCompliancePage';
import { SessionNotesExportPage } from '@/features/reporting/SessionNotesExportPage';
import { EvvReportPage } from '@/features/reporting/EvvReportPage';
import { GoalMasteryPage } from '@/features/reporting/GoalMasteryPage';
import { ClientPhiAccessPage } from '@/features/reporting/ClientPhiAccessPage';
import { ClaimDenialPage } from '@/features/reporting/ClaimDenialPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { ManageUsersInHomePage } from '@/features/users/ManageUsersInHomePage';
import { ParentPortalPage } from '@/features/portal/ParentPortalPage';
import { NotFoundPage } from '@/features/errors/NotFoundPage';

function guard(perm: string, element: React.ReactNode) {
    return <RequireAuth perm={perm}>{element}</RequireAuth>;
}

export const router = createBrowserRouter([
    { path: '/auth/callback', element: <AuthCallbackPage /> },
    { path: '/auth/logout',   element: <AuthLogoutPage /> },
    { path: '/auth/error',    element: <AuthErrorPage /> },
    { path: '/impersonate', element: <ImpersonatePage /> },
    // Cognito-mode impersonation entry/return points (per ADR 0007).
    // Stub mode keeps using /impersonate#token=... above.
    { path: '/impersonate-start',    element: <ImpersonateStartPage /> },
    { path: '/impersonate-callback', element: <ImpersonateCallbackPage /> },
    {
        path: '/',
        element: <AppShell />,
        children: [
            { index: true, element: <DashboardPage /> },
            { path: 'clients',          element: guard('view_assigned_clients', <ClientsListPage />) },
            { path: 'clients/new',      element: guard('add_clients',           <ClientNewPage />) },
            { path: 'clients/:id',      element: guard('view_assigned_clients', <ClientDetailPage />) },
            { path: 'clients/:id/edit', element: guard('edit_clients',          <ClientEditPage />) },
            { path: 'scheduling',      element: guard('view_scheduling',        <SchedulingPage />) },
            { path: 'sessions/:id',    element: guard('view_scheduling',        <SessionPage />) },
            { path: 'configuration',   element: guard('manage_settings',        <ConfigurationPage />) },
            { path: 'configuration/note-types/new', element: guard('manage_settings', <NoteTypeBuilderPage />) },
            { path: 'configuration/note-types/:id', element: guard('manage_settings', <NoteTypeBuilderPage />) },
            // Stripe Connect onboarding return + refresh URLs. Practice
            // admin initiates from BillingSettingsPanel; Stripe redirects
            // back here. Per Phase 0b of pma-stripe-payments.md.
            { path: 'settings/stripe/return',  element: guard('manage_settings', <StripeReturnPage />) },
            { path: 'settings/stripe/refresh', element: guard('manage_settings', <StripeRefreshPage />) },
            { path: 'treatment-plans',              element: guard('view_treatment_plans', <TreatmentPlansPage />) },
            { path: 'treatment-plans/programs/:id', element: guard('view_treatment_plans', <ProgramDetailPage />) },
            { path: 'treatment-plans/plans/new',    element: guard('edit_treatment_plans', <PlanBuilderPage />) },
            { path: 'treatment-plans/plans/:id',    element: guard('view_treatment_plans', <PlanBuilderPage />) },
            { path: 'data-analysis',             element: guard('view_treatment_plans', <AnalyticsPage />) },
            { path: 'data-analysis/:clientId',   element: guard('view_treatment_plans', <AnalyticsPage />) },
            {
                path: 'billing',
                element: guard('view_billing', <BillingLayout />),
                children: [
                    { index: true,      element: <Navigate to="/billing/claims" replace /> },
                    { path: 'claims',   element: <BillingPage /> },
                    { path: 'invoices', element: <InvoicesTab /> },
                ],
            },
            { path: 'billing/new',                 element: guard('edit_billing',   <BillingNewPage />) },
            { path: 'billing/submissions',         element: guard('submit_claims',  <BillingSubmissionsPage />) },
            { path: 'billing/submissions/:id',     element: guard('submit_claims',  <BillingSubmissionDetailPage />) },
            { path: 'billing/invoices/:id',        element: guard('view_billing',   <InvoiceDetailPage />) },
            { path: 'billing/:id',                 element: guard('view_billing',   <BillingDetailPage />) },
            { path: 'reporting',                   element: guard('view_reports',   <ReportingPage />) },
            { path: 'reporting/billing-ar',        element: guard('view_reports',   <BillingArPage />) },
            { path: 'reporting/session-activity',  element: guard('view_reports',   <SessionActivityPage />) },
            { path: 'reporting/cancellation-rollup', element: guard('view_reports', <CancellationRollupPage />) },
            { path: 'reporting/auth-utilization',  element: guard('view_reports',   <AuthUtilizationPage />) },
            { path: 'reporting/staff-performance', element: guard('view_reports',   <StaffPerformancePage />) },
            { path: 'reporting/payroll',           element: guard('view_payroll',   <PayrollPage />) },
            { path: 'reporting/availability-overlap', element: guard('view_reports', <AvailabilityOverlapPage />) },
            { path: 'reporting/supervision',       element: guard('view_supervision', <SupervisionCompliancePage />) },
            { path: 'reporting/session-notes',     element: guard('view_notes',     <SessionNotesExportPage />) },
            { path: 'reporting/evv',               element: guard('view_scheduling', <EvvReportPage />) },
            { path: 'reporting/goal-mastery',      element: guard('view_treatment_plans', <GoalMasteryPage />) },
            { path: 'reporting/client-phi-access', element: guard('view_audit_log', <ClientPhiAccessPage />) },
            { path: 'reporting/claim-denials',     element: guard('view_billing',   <ClaimDenialPage />) },
            { path: 'portal',             element: guard('view_portal', <ParentPortalPage />) },
            { path: 'portal/:clientId',   element: guard('view_portal', <ParentPortalPage />) },
            // Phase 15 retired PMA's user-management UI. The legacy
            // /users (and /users/roles/*) URLs land on a redirect-style
            // info page that points the user at Home; these stay
            // registered so bookmarked links don't 404.
            { path: 'users',           element: <ManageUsersInHomePage /> },
            { path: 'users/roles/new', element: <ManageUsersInHomePage /> },
            { path: 'users/roles/:id', element: <ManageUsersInHomePage /> },
            { path: 'audit-log',       element: guard('view_audit_log',  <AuditLogPage />) },
            { path: 'settings',        element: <Navigate to="/configuration" replace /> },
        ],
    },
    { path: '*', element: <NotFoundPage /> },
]);
